import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { PERMISSIONS } from '@/types/auth';
import type { ConfirmWeightProductionJobRequest, WeightProductionJobPendingLineDto, WeightProductionJobPendingOrderDto } from '@/types/weightProduction';

/**
 * Printing a label at the Kiosk no longer moves stock for a plan-based weighing on its own — a
 * supervisor reviews each plan here, item-wise, and confirming a line is what actually posts its
 * weighings to stock (see WeightProductionEntryService.ConfirmEntryAsync /
 * WeightProductionJobCompletionService.ConfirmAsync). Direct/no-plan weighings never appear here;
 * they still post immediately, there being no plan to review them against.
 */
export default function WeightProductionJobCompletionPage() {
  const { t } = useTranslation('weightProduction');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['weight-production-job-completion-pending'],
    queryFn: async () => (await apiClient.get<WeightProductionJobPendingOrderDto[]>('/weight-production-job-completion/pending')).data,
  });

  // Selection is keyed by line id — checking a line selects every one of its still-unconfirmed
  // entries, matching the "item-wise" granularity this screen is meant to work at.
  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(new Set());
  // The Weighings count is also a shortcut to see exactly which barcodes make up that number —
  // useful before confirming a line, since "10" alone doesn't say which physical labels those are.
  const [viewingLine, setViewingLine] = useState<WeightProductionJobPendingLineDto | null>(null);

  const toggleLine = (lineId: string) => {
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const toggleOrder = (order: WeightProductionJobPendingOrderDto) => {
    const lineIds = order.lines.map((l) => l.weightProductionOrderLineId);
    const allSelected = lineIds.every((id) => selectedLineIds.has(id));
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      lineIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const selectedEntryIds = orders
    .flatMap((o) => o.lines)
    .filter((l) => selectedLineIds.has(l.weightProductionOrderLineId))
    .flatMap((l) => l.entries.map((e) => e.id));

  const confirmMutation = useMutation({
    mutationFn: () => {
      const payload: ConfirmWeightProductionJobRequest = { entryIds: selectedEntryIds };
      return apiClient.post('/weight-production-job-completion/confirm', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weight-production-job-completion-pending'] });
      queryClient.invalidateQueries({ queryKey: ['weight-production-entries'] });
      setSelectedLineIds(new Set());
    },
  });

  return (
    <>
      <PageHeader title={t('jobCompletion.title')} subtitle={t('jobCompletion.subtitle')} />

      {!isLoading && orders.length === 0 && (
        <Alert severity="info">{t('jobCompletion.nothingPending')}</Alert>
      )}

      <Stack spacing={2}>
        {orders.map((order) => {
          const lineIds = order.lines.map((l) => l.weightProductionOrderLineId);
          const allSelected = lineIds.length > 0 && lineIds.every((id) => selectedLineIds.has(id));
          const someSelected = lineIds.some((id) => selectedLineIds.has(id));
          return (
            <Card variant="outlined" key={order.orderId}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{order.orderNumber}</Typography>
                    <Typography variant="caption" color="text.secondary">{order.warehouseName}</Typography>
                  </Box>
                  <Button size="small" onClick={() => toggleOrder(order)}>
                    {allSelected ? t('jobCompletion.deselectAll') : t('jobCompletion.selectAll')}
                  </Button>
                </Stack>

                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={someSelected && !allSelected}
                            onChange={() => toggleOrder(order)}
                          />
                        </TableCell>
                        <TableCell>{t('jobCompletion.material')}</TableCell>
                        <TableCell align="right">{t('jobCompletion.pendingQty')}</TableCell>
                        <TableCell align="right">{t('jobCompletion.entryCount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.lines.map((line) => (
                        <TableRow
                          key={line.weightProductionOrderLineId} hover
                          selected={selectedLineIds.has(line.weightProductionOrderLineId)}
                          onClick={() => toggleLine(line.weightProductionOrderLineId)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox size="small" checked={selectedLineIds.has(line.weightProductionOrderLineId)} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{line.materialCode}</Typography>
                            <Typography variant="caption" color="text.secondary">{line.materialName}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            {line.pendingQty} {line.planBasis === 'Quantity' ? t('kiosk.unitPcs') : ''}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small" label={line.entries.length} clickable
                              onClick={(e) => { e.stopPropagation(); setViewingLine(line); }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {orders.length > 0 && (
        <IfPermitted permission={PERMISSIONS.WEIGHT_PRODUCTION_MANAGE}>
          <Box sx={{ position: 'sticky', bottom: 16, mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained" size="large"
              disabled={selectedEntryIds.length === 0 || confirmMutation.isPending}
              onClick={() => confirmMutation.mutate()}
            >
              {confirmMutation.isPending
                ? t('jobCompletion.confirming')
                : t('jobCompletion.confirmButton', { count: selectedEntryIds.length })}
            </Button>
          </Box>
        </IfPermitted>
      )}

      <Dialog open={Boolean(viewingLine)} onClose={() => setViewingLine(null)} maxWidth="xs" fullWidth>
        {viewingLine && (
          <>
            <DialogTitle>
              {viewingLine.materialCode}
              <Typography variant="body2" color="text.secondary">{viewingLine.materialName}</Typography>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('jobCompletion.barcode')}</TableCell>
                    <TableCell align="right">{t('jobCompletion.weight')}</TableCell>
                    <TableCell align="right">{t('jobCompletion.produced')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewingLine.entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{entry.barcodeNo}</TableCell>
                      <TableCell align="right">{entry.actualWeight} {entry.weightUom}</TableCell>
                      <TableCell align="right">{formatDate(entry.createdDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setViewingLine(null)}>{t('jobCompletion.close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
